import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRangePicker as DualCalendarPicker } from "@pervasivemind/bi-components";
import type { DatePreset, DatePresetCompute } from "./types";
import {
  buildDomain,
  DualDateSlider,
  expandBoundsForSelection,
  nearestIndex,
  type DateSliderStep,
} from "./date-slider-core";
import { resolveLocalized } from "./i18n-resolve";
import { getI18n, toLang } from "./i18n-binding";

/**
 * Date-range filter for the dashboard filter bar — shared between
 * the hp-portal and the builder preview (byte-identical mirror).
 *
 * Iter 14 rewrite: thin wrapper around bi-components' dual-calendar
 * DateRangePicker. Adds quick-pick chips at the top of the popover
 * via the lib's ``presets`` prop so the common cases stay one-click
 * while custom date ranges get a real two-month calendar picker —
 * the previous 619-LOC bespoke picker only exposed presets +
 * numeric "last N units" inputs, which the user found unmanageable
 * for custom ranges.
 *
 * PR4: when the engine serves the column's ``[min, max]`` day bounds
 * (``fetch_filter_options`` now emits them for every date-window
 * filter type), the popover additionally embeds a dual-handle date
 * slider between the chips and the Inicio/Fin pills. Dragging updates
 * the PENDING selection (pills + calendars follow live); the popover's
 * existing "Aplicar" button commits it. Without bounds the popover is
 * byte-identical to the pre-PR4 layout.
 *
 * i18n (2026-07-14 audit): every user-facing string of the popover
 * (Start/End pills, Apply, Clear, footer hints, aria-labels) is
 * resolved HERE via ``useTranslation()`` and passed to the lib picker
 * through its ``strings`` prop; the active locale is passed as
 * ``locale`` so the calendar month/day names come from
 * ``Intl.DateTimeFormat``. The bi-components picker stays i18n-free
 * (pure JSX package) — it only receives strings by props.
 *
 * Value encoding (all JSON-stringified, stored inside the
 * ``activeFilters: Record<string, string>`` dict, decoded by the
 * backend's ``_decode_date_filter``):
 *
 *   relative → ``{"relative": {"unit": "days", "length": 30}}``
 *   fixed    → ``{"start": "2026-01-15", "end": "2026-04-15"}``
 *   preset   → ``{"preset": "today" | ...}`` (legacy: still decoded
 *               on read for compat with old activeFilters dumps;
 *               new picks emit relative or fixed).
 *
 * Empty string ``""`` is the sentinel meaning "user cleared this
 * filter" — backend skips the filter (does NOT re-apply the
 * report-config default).
 */

const RELATIVE_UNITS = ["days", "weeks", "months"] as const;
type RelativeUnit = (typeof RELATIVE_UNITS)[number];

// Two preset kinds:
//   "relative" — last N days/weeks/months; encoded as ``{relative: {…}}``
//   "named"    — a fixed preset id resolved by the engine (HP fiscal
//                quarters, etc.); encoded as ``{preset: "<id>"}``
interface PresetSpec {
  id: string;
  label: string;
  kind: "relative" | "named";
  unit?: RelativeUnit;
  length?: number;
  // Satisfies bi-components' ``DateRangePreset`` index signature.
  [extra: string]: unknown;
}

// NO HARDCODED CHIP LIST (per user rule, 2026-05-25). Chips ONLY come
// from the per-report ``configPresets`` prop, which the engine writes
// to ``dashboard_config.global_filters[i].presets``. If a filter has
// no presets configured, NO chips are shown — admin must configure
// them via the builder UI.

/** Build chips from per-report config presets (iter 26). Label is
 * resolved via the bilingual ``DisplayLabel`` falling back to the id. */
function buildPresetsFromConfig(
  configPresets: ReadonlyArray<DatePreset>,
  overrides: Partial<Record<string, string>> | undefined,
): PresetSpec[] {
  const locale = toLang(getI18n().language);
  return configPresets.map((p) => ({
    id: p.id,
    kind: "named" as const,
    label:
      overrides?.[p.id] ??
      (p.label ? resolveLocalized(p.label, locale) : null) ??
      p.id,
  }));
}

/** Resolve a config preset id to its (start, end) range by dispatching
 * on ``compute.kind`` — mirror of the engine's ``_compute_preset`` so
 * the chip text is correct without a backend roundtrip.
 *
 * Exported (pure function) so ``DateRangeSlider`` can position its
 * handles when the filter's default_value is a ``{"preset": id}``
 * shape. Display-convention mirror only — the engine remains the
 * authority for the actual SQL window. */
export function resolveConfigPreset(
  presetId: string,
  configPresets: ReadonlyArray<DatePreset>,
): [Date, Date] | null {
  const def = configPresets.find((p) => p.id === presetId);
  if (!def) return null;
  const today = new Date();
  return computeRange(def.compute, today);
}

function computeRange(
  compute: DatePresetCompute,
  today: Date,
): [Date, Date] | null {
  switch (compute.kind) {
    case "absolute_range": {
      const start = parseRelDate(compute.params.start, today);
      const end = parseRelDate(compute.params.end, today);
      if (!start || !end) return null;
      return [start, end];
    }
    case "relative_days": {
      const start = new Date(today);
      start.setDate(start.getDate() - compute.params.length);
      return [start, today];
    }
    case "relative_months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - compute.params.length);
      start.setDate(1);
      const end = new Date(today.getFullYear(), today.getMonth(), 1);
      return [start, end];
    }
    case "hp_fiscal_q": {
      const { offset_back, duration_quarters, include_open } = compute.params;
      const calMonth = today.getMonth();
      const calYear = today.getFullYear();
      const curFy = calYear + (calMonth >= 10 ? 1 : 0);
      const fiscalMonth = ((calMonth - 10 + 12) % 12) + 1;
      const curFq = Math.floor((fiscalMonth - 1) / 3) + 1;
      const walk = (n: number): [number, number] => {
        const idx = curFy * 4 + (curFq - 1) - n;
        return [Math.floor(idx / 4), (idx % 4) + 1];
      };
      const fqStart = (fy: number, fq: number): Date => {
        let m: number; let yo: number;
        switch (fq) {
          case 1: m = 10; yo = -1; break; // Nov
          case 2: m = 1;  yo = 0;  break; // Feb
          case 3: m = 4;  yo = 0;  break; // May
          default: m = 7; yo = 0;  break; // Aug
        }
        return new Date(fy + yo, m, 1);
      };
      const [startFy, startFq] = walk(offset_back);
      const start = fqStart(startFy, startFq);
      const [endFy, endFq] = walk(offset_back - duration_quarters);
      const endIdx = endFy * 4 + (endFq - 1);
      const curIdx = curFy * 4 + (curFq - 1);
      const end = include_open && endIdx > curIdx ? today : fqStart(endFy, endFq);
      return [start, end];
    }
    default:
      return null;
  }
}

function parseRelDate(val: string, today: Date): Date | null {
  const s = (val || "").trim().toLowerCase();
  if (s === "today") return today;
  if (s === "tomorrow") {
    const t = new Date(today);
    t.setDate(t.getDate() + 1);
    return t;
  }
  if (s === "yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return y;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
}

interface DateRangePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  embedded: boolean;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  /**
   * Reserved for a future time-of-day picker mode. Currently
   * ignored — the wrapper stays day-granular regardless. Kept in
   * the prop shape so the builder's ``GenericFilterBar``, which
   * derives this from a column's DuckDB type, doesn't need to
   * branch on whether the wrapper accepts it.
   */
  hasTime?: boolean;
  /**
   * Optional override map for the quick-pick chip labels, keyed by
   * preset id (``last_7_days``, ``last_30_days``, ``last_3_months``,
   * ``last_12_months``). When a value is omitted, the wrapper falls
   * back to the Spanish default. The portal passes these from
   * i18next so the chips localize with the rest of the UI; the
   * builder preview accepts the defaults.
   *
   * Kept as a prop instead of importing ``useTranslation`` directly
   * so this file stays a byte-for-byte mirror with the builder
   * (which doesn't ship ``react-i18next``).
   */
  presetLabels?: Partial<Record<string, string>>;
  /**
   * Iter 26 — per-report preset library passed via the filter config.
   * When supplied (non-empty), the picker shows ONE chip per entry
   * (in order) and skips the hardcoded ``PRESET_SPECS_BASE`` list.
   * When empty/null, falls back to the hardcoded HP chips.
   */
  configPresets?: ReadonlyArray<DatePreset> | null;
  /**
   * The filter column's global ``[min_iso, max_iso]`` day bounds,
   * served by the engine's ``fetch_filter_options`` for every
   * date-window filter type. When present (2 valid entries) the
   * popover grows a dual-handle slider row between the preset chips
   * and the Inicio/Fin pills. Empty / undefined → no slider row: the
   * popover renders exactly as before (zero regression for reports
   * whose backend predates the bounds payload).
   */
  bounds?: readonly string[];
  /**
   * Snap granularity for the in-popover slider's domain
   * (``buildDomain``): handles land on days, ISO-week Mondays, or
   * month firsts. Comes from the filter config's ``slider_step``;
   * the weekly gating reports are the primary consumer.
   */
  sliderStep?: DateSliderStep;
  /**
   * Render the 520px calendar panel to ``document.body`` (fixed,
   * positioned against the trigger) instead of an in-flow popover.
   * The filter cajón drawer sets this so the wide panel escapes the
   * 360px scrolling drawer. Default false — the banner is unchanged.
   */
  portal?: boolean;
}

interface DecodedValue {
  start: Date | null;
  end: Date | null;
  presetId?: string;
}

// ── Value decode / encode ─────────────────────────────────

// Exported for DateRangeSlider — same local-day ISO convention as the
// calendar picker, so both widgets emit byte-identical {"start","end"}.
export function isoFromDate(d: Date): string {
  // Anchor the ISO date in local-time year/month/day to match the
  // user's intent (the calendar shows local dates).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateFromIso(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Exported for DateRangeSlider — client-side mirror of the engine's
// ``{"relative": {...}}`` resolution, used only to position handles.
export function resolveRelative(unit: RelativeUnit, length: number): [Date, Date] {
  const end = new Date();
  const start = new Date(end);
  if (unit === "days") start.setDate(start.getDate() - length);
  else if (unit === "weeks") start.setDate(start.getDate() - length * 7);
  else if (unit === "months") start.setMonth(start.getMonth() - length);
  return [start, end];
}

// NO HARDCODED PRESET RESOLVERS (per user rule, 2026-05-25).
// All preset resolution flows through ``resolveConfigPreset`` which
// reads ``compute.kind`` from the per-report config. If a preset id
// is not in the filter's config, the picker returns null and the
// chip stays unselected.

function decodeValue(
  raw: string,
  configPresets?: ReadonlyArray<DatePreset> | null,
): DecodedValue {
  if (!raw) return { start: null, end: null };
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return { start: null, end: null };
  }
  if (obj && typeof obj === "object") {
    if (typeof obj.start === "string" && typeof obj.end === "string") {
      return {
        start: dateFromIso(obj.start),
        end: dateFromIso(obj.end),
      };
    }
    if (obj.relative && typeof obj.relative === "object") {
      const rel = obj.relative as { unit?: string; length?: number };
      if (
        rel.unit &&
        RELATIVE_UNITS.includes(rel.unit as RelativeUnit) &&
        typeof rel.length === "number"
      ) {
        const unit = rel.unit as RelativeUnit;
        const [start, end] = resolveRelative(unit, rel.length);
        // No hardcoded id lookup — the active chip only highlights
        // when the value matches a config preset (handled below).
        return { start, end };
      }
    }
    if (typeof obj.preset === "string") {
      // NO FALLBACK to hardcoded resolvers (per user rule). The
      // preset MUST be defined in the filter's ``configPresets``;
      // if not, the value is silently invalid (returns no dates).
      if (configPresets && configPresets.length > 0) {
        const fromCfg = resolveConfigPreset(obj.preset, configPresets);
        if (fromCfg) {
          return {
            start: fromCfg[0],
            end: fromCfg[1],
            presetId: obj.preset,
          };
        }
      }
    }
  }
  return { start: null, end: null };
}

function encodeFixed(start: Date, end: Date): string {
  return JSON.stringify({ start: isoFromDate(start), end: isoFromDate(end) });
}

function encodeRelative(unit: RelativeUnit, length: number): string {
  return JSON.stringify({ relative: { unit, length } });
}

// ── Main component ────────────────────────────────────────

export function DateRangePicker({
  value,
  onChange,
  placeholder,
  embedded,
  disabled,
  label,
  required,
  presetLabels,
  configPresets,
  bounds,
  sliderStep = "week",
  portal = false,
}: DateRangePickerProps) {
  const decoded = useMemo(
    () => decodeValue(value, configPresets),
    [value, configPresets],
  );
  // Localize chip labels via i18next when a ``daterange.preset.<id>``
  // key is registered in the consumer's dict. The per-report config's
  // bilingual ``label`` still wins downstream — this only kicks in
  // when the config has no label, or to provide global EN/ES for
  // shared ids like ``hp_q_actual`` across reports.
  const { t, i18n } = useTranslation();
  // Active locale drives the lib picker's Intl month/day names; every
  // other user-facing string inside the popover is passed translated
  // (i18n audit 2026-07-14) so the apps' dicts stay the source of truth.
  const pickerLocale = toLang(i18n.language);
  const pickerStrings = useMemo(
    () => ({
      selectRange: t("daterange.placeholder"),
      start: t("daterange.start"),
      end: t("daterange.end"),
      pending: t("daterange.pending"),
      clear: t("daterange.clear"),
      apply: t("daterange.apply"),
      hintPickStart: t("daterange.hintPickStart"),
      hintPickEnd: t("daterange.hintPickEnd"),
      goToStartMonth: t("daterange.goToStartMonth"),
      goToEndMonth: t("daterange.goToEndMonth"),
      startPending: t("daterange.startPending"),
      endPending: t("daterange.endPending"),
    }),
    [t, i18n.language],
  );
  const i18nLabels = useMemo(() => {
    const out: Record<string, string> = {};
    for (const p of configPresets ?? []) {
      const key = `daterange.preset.${p.id}`;
      const translated = t(key);
      if (translated && translated !== key) out[p.id] = translated;
    }
    return out;
  }, [t, i18n.language, configPresets]);
  const presets = useMemo(() => {
    const labels = { ...i18nLabels, ...(presetLabels ?? {}) };
    if (configPresets && configPresets.length > 0) {
      return buildPresetsFromConfig(configPresets, labels);
    }
    // NO chips when the filter has no config presets (per user rule:
    // NEVER fallback to hardcoded). Admin must configure via builder.
    return [];
  }, [configPresets, i18nLabels, presetLabels]);

  // Local "pending start" state for the picking-in-progress window.
  // The bi-components dual-calendar picker is fully controlled: when
  // the user clicks a start date, it fires ``onSelect(start, null)``
  // and expects the parent to pass that start back as a prop. Without
  // that round-trip the second click compares ``date < null`` (always
  // false) and fires ``onSelect(null, endDate)`` — neither commits a
  // valid range. So we hold the intermediate start here, render it to
  // the picker, and only push to the upstream filter when both dates
  // are present. Resets whenever an external value change lands (a
  // chip click or the parent re-publishing the active filters).
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  useEffect(() => {
    setPendingStart(null);
  }, [value]);

  // Pending window driven by the in-popover slider. Dragging the
  // handles updates this (live — the Inicio/Fin pills and the
  // calendars re-render from it on every step) but does NOT push
  // upstream; the popover's "Aplicar" button is the commit gesture,
  // consistent with the rest of the popover flow. Dismissing the
  // popover without applying discards it (via ``onOpenChange``), and
  // any external value change supersedes it.
  const [pendingRange, setPendingRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  useEffect(() => {
    setPendingRange(null);
  }, [value]);

  const displayStart = pendingStart ?? pendingRange?.start ?? decoded.start;
  const displayEnd = pendingStart ? null : pendingRange?.end ?? decoded.end;
  const displayPresetId =
    pendingStart || pendingRange ? undefined : decoded.presetId;

  // ── In-popover slider (PR4 + UX r3 zoom) ────────────────
  // Snap-point domain over the column's bounds, RE-SCOPED to the
  // committed selection (UX r3): the preset chips / calendar define
  // the slider's zoom — the track spans the committed selection
  // widened to double its length (clamped to the universal bounds),
  // so the slider becomes fine adjustment inside that window. "All"
  // or no selection → the full bounds. Empty domain (no bounds
  // served / malformed) → no slider row at all.
  //
  // ANTI-FREEZE INVARIANT (iter 36 / PR #25 lineage): the domain must
  // re-anchor ONLY when the selection changes from OUTSIDE the slider
  // (preset chip, calendar commit, external value). It therefore
  // derives exclusively from the COMMITTED value (``decoded``, via
  // stable ISO-string deps) — NEVER from ``pendingRange`` /
  // ``pendingStart``, which the slider's own onChange mutates on
  // every drag step. A live drag can never move its own track, and
  // committed-value changes only happen with no drag in flight
  // (Aplicar / chip / calendar click all end or preclude a drag).
  const committedStartIso = decoded.start ? isoFromDate(decoded.start) : null;
  const committedEndIso = decoded.end ? isoFromDate(decoded.end) : null;
  const domain = useMemo(
    () =>
      buildDomain(
        expandBoundsForSelection(
          bounds,
          committedStartIso,
          committedEndIso,
          sliderStep,
        ),
        sliderStep,
      ),
    [bounds, sliderStep, committedStartIso, committedEndIso],
  );
  const maxIdx = domain.length - 1;

  // Handle indices track the DISPLAYED (pending-or-committed) window,
  // so calendar clicks and chip picks reposition the handles too —
  // the bidirectional sync is just "everything renders the display
  // window". No selection at all → handles rest at the extremes.
  const [sliderLo, sliderHi] = useMemo<[number, number]>(() => {
    if (domain.length === 0) return [0, 0];
    const lo = displayStart
      ? nearestIndex(domain, isoFromDate(displayStart))
      : 0;
    const hi = displayEnd ? nearestIndex(domain, isoFromDate(displayEnd)) : maxIdx;
    return lo <= hi ? [lo, hi] : [hi, lo];
  }, [domain, maxIdx, displayStart, displayEnd]);

  const sliderSlot =
    domain.length > 0 ? (
      <DualDateSlider
        domain={domain}
        lo={sliderLo}
        hi={sliderHi}
        labels="extremes"
        onChange={(lo, hi) => {
          const start = dateFromIso(domain[lo]!);
          const end = dateFromIso(domain[hi]!);
          if (!start || !end) return;
          // Slider movement supersedes a half-finished calendar pick.
          setPendingStart(null);
          // Identity-preserving: when the sorted window didn't change
          // (dragging a crossed handle over the same snap point), keep
          // the previous object so the popover subtree (two calendars
          // + pills) doesn't re-render once per input event.
          setPendingRange((prev) =>
            prev &&
            isoFromDate(prev.start) === domain[lo] &&
            isoFromDate(prev.end) === domain[hi]
              ? prev
              : { start, end },
          );
        }}
      />
    ) : undefined;

  // Preset chips passed to the lib picker. The chip ``label`` here
  // is the visible Spanish string; the ``unit`` + ``length`` fields
  // are pass-through payload that the wrapper reads back in
  // ``onPresetSelect`` to encode the JSON value.
  const composedLabel = label ? label + (required ? " *" : "") : undefined;

  if (disabled) {
    // The lib picker doesn't expose a disabled-trigger variant; we
    // simulate it with a static button so the filter bar layout
    // doesn't shift. It shows the COMMITTED window (a locked filter
    // still filters the data — audit #207), in the same
    // `start  →  end` shape as the enabled trigger; with no decoded
    // window it shows the placeholder, like the enabled trigger does.
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        {composedLabel && (
          <label
            className={`text-2xs font-semibold uppercase tracking-wide ${
              embedded ? "text-white/75" : "text-neutral-500"
            }`}
          >
            {composedLabel}
          </label>
        )}
        <div
          className={`flex h-7 w-full cursor-not-allowed items-center rounded-lg border px-2 text-xs font-medium opacity-60 ${
            embedded
              ? "border-white/20 bg-white/10 text-white/50"
              : "border-neutral-300 bg-neutral-50 text-neutral-500"
          }`}
        >
          <span className="truncate">
            {decoded.start && decoded.end
              ? `${isoFromDate(decoded.start)}  →  ${isoFromDate(decoded.end)}`
              : placeholder}
          </span>
        </div>
      </div>
    );
  }

  return (
    <DualCalendarPicker
      label={composedLabel}
      start={displayStart}
      end={displayEnd}
      embedded={embedded}
      placeholder={placeholder}
      compact
      portal={portal}
      locale={pickerLocale}
      strings={pickerStrings}
      presets={presets}
      activePresetId={displayPresetId}
      sliderSlot={sliderSlot}
      // "Aplicar" commits the slider's pending window (if any). The
      // calendar path is untouched — it already committed on the
      // second day click, so applying with no slider pending is the
      // same close-only click it always was.
      onApply={() => {
        if (!pendingRange) return;
        onChange(encodeFixed(pendingRange.start, pendingRange.end));
        setPendingRange(null);
      }}
      // Dismissing the popover without applying (outside click /
      // trigger toggle) discards the uncommitted slider selection so
      // the trigger never shows a range that was never applied.
      onOpenChange={(open: boolean) => {
        if (!open) setPendingRange(null);
      }}
      onSelect={(s, e) => {
        // Both null → user clicked "Limpiar" in the popover.
        if (!s && !e) {
          setPendingStart(null);
          setPendingRange(null);
          onChange("");
          return;
        }
        // Both set → user picked a fixed range via the dual calendar.
        if (s && e) {
          setPendingStart(null);
          setPendingRange(null);
          onChange(encodeFixed(s, e));
          return;
        }
        // Mid-pick (start-only): keep the start visible in the picker
        // so the next click can land. Don't push to the upstream
        // filter yet — backend rejects half-ranges. A calendar pick
        // supersedes any slider-pending window.
        if (s && !e) {
          setPendingStart(s);
          setPendingRange(null);
          return;
        }
      }}
      onPresetSelect={(p) => {
        const spec = p as unknown as PresetSpec;
        setPendingStart(null);
        setPendingRange(null);
        if (spec.kind === "named") {
          // Named presets (HP fiscal quarter, "all time", etc.) encode
          // as ``{preset: "<id>"}`` — the engine resolves them on the
          // server with the same logic the local mirror used to draw
          // the chip range.
          onChange(JSON.stringify({ preset: spec.id }));
          return;
        }
        // Relative presets encode as ``{relative: {unit, length}}``.
        if (spec.unit && typeof spec.length === "number") {
          onChange(encodeRelative(spec.unit, spec.length));
        }
      }}
    />
  );
}
